<template>
  <div class="forwarder-list-container">
    <box>
      <data-table
      ref="dataTable"
      :data="forwarders"
      :columns="columns"
      :loading="loading"
      :searchable="true"
      :search-placeholder="$t('forwarders.searchPlaceholder')"
      :items-per-page-options="[10, 15, 30, 50]"
      :default-items-per-page="currentPageSize"
      :default-sort-by="'last_update'"
      :default-sort-order="'desc'"
      :striped="true"
      :hoverable="true"
      :server-side="true"
      :total-items="totalItems"
      @search="handleSearch"
      @sort="handleSort"
      @page-change="handlePageChange"
      @page-size-change="handlePageSizeChange"
    >
      <!-- STT Column -->
      <template #cell-stt="{ index }">
        <span>{{ index + 1 }}</span>
      </template>

      <!-- Status Column -->
      <template #cell-status="{ value }">
        <span v-if="value" class="tag is-success is-light">
          {{ $t('active') }}
        </span>
        <span v-else class="tag is-warning is-light">
          {{ $t('inactive') }}
        </span>
      </template>

      <!-- Active Modules Column -->
      <template #cell-active_modules="{ row }">
        <div class="tags">
          <span v-if="row.system_log && row.system_log.enabled" class="tag is-info is-light">
            {{ $t('forwarders.systemLog') }}
          </span>
          <span v-if="row.resource_monitor && row.resource_monitor.enabled" class="tag is-info is-light">
            {{ $t('forwarders.resourceMonitor') }}
          </span>
          <span v-if="row.file_log && row.file_log.enabled" class="tag is-info is-light">
            {{ $t('forwarders.fileLog') }}
          </span>
          <span v-if="!hasActiveModules(row)" class="has-text-grey-light">
            {{ $t('none') }}
          </span>
        </div>
      </template>

      <!-- Created At Column -->
      <template #cell-created_at="{ value }">
        <span v-if="isValidDate(value)">
          {{ formatDate(value) }}
        </span>
        <span v-else class="has-text-grey-light">
          N/A
        </span>
      </template>

      <!-- Last Event Column -->
      <template #cell-last_event="{ value }">
        <span v-if="value">
          {{ formatDate(value) }}
        </span>
        <span v-else class="has-text-grey-light">
          {{ $t('none') }}
        </span>
      </template>

      <!-- Actions Column -->
      <template #cell-actions="{ row }">
        <div class="has-text-centered">
          <button 
            class="button is-text is-small" 
            :title="$t('edit')"
            @click.prevent="handleEdit(row)"
          >
            <octicon :icon="icons.pencil" />
          </button>
          <button 
            class="button is-text is-small"
            :class="row.enabled ? 'has-text-warning' : 'has-text-success'"
            :title="row.enabled ? $t('deactivate') : $t('activate')"
            @click.prevent="handleToggleStatus(row)"
          >
            <octicon :icon="row.enabled ? icons.circleSlash : icons.play" />
          </button>
          <button 
            class="button is-text is-small has-text-danger" 
            :title="$t('delete')"
            @click.prevent="handleDelete(row)"
          >
            <octicon :icon="icons.trashcan" />
          </button>
        </div>
      </template>
    </data-table>
    </box>
  </div>
</template>

<script>
import DataTable from '@/components/common/DataTable.vue'
import { format } from 'date-fns'
import * as icons from 'octicons-vue'

export default {
  name: 'ForwarderList',
  components: { 
    DataTable
  },
  props: {
    filters: {
      type: Object,
      default: () => ({
        statuses: [],
        modules: []
      })
    }
  },
  data() {
    return {
      forwarders: [],
      loading: false,
      totalItems: 0,
      currentPage: 1,
      currentPageSize: 10,
      currentSort: {
        by: 'last_update',
        order: 'desc'
      },
      currentSearch: ''
    }
  },
  computed: {
    icons() {
      return icons
    },
    columns() {
      return [
        {
          key: 'stt',
          label: this.$t('stt'),
          width: '60px',
          sortable: false,
          align: 'center'
        },
        {
          key: 'name',
          field: 'name',
          label: this.$t('name'),
          sortable: true,
          truncate: true,
          width: '120px',
        },
        {
          key: 'description',
          field: 'description',
          label: this.$t('description'),
          sortable: false,
          truncate: true,
          width: '120px',
        },
        {
          key: 'status',
          field: 'enabled',
          label: this.$t('status.status'),
          width: '120px',
          sortable: true,
          align: 'center',
          defaultSortOrder: 'desc'
        },
        {
          key: 'active_modules',
          label: this.$t('activeModules'),
          width: '200px',
          sortable: false
        },
        {
          key: 'created_at',
          field: 'created_at',
          label: this.$t('createdAt'),
          width: '150px',
          sortable: true
        },
        {
          key: 'last_event',
          field: 'last_update',
          label: this.$t('lastEvent'),
          width: '120px',
          sortable: true
        },
        {
          key: 'actions',
          label: this.$t('actions'),
          width: '120px',
          sortable: false,
          align: 'center'
        }
      ]
    }
  },
  watch: {
    filters: {
      handler() {
        this.currentPage = 1
        this.loadForwarders()
      },
      deep: true
    }
  },
  mounted() {
    this.loadForwarders()
  },
  methods: {
    formatDate(dateString) {
      if (!dateString) return this.$t('none')
      try {
        return format(new Date(dateString), 'dd/MM/yyyy HH:mm:ss')
      } catch (error) {
        console.error('Error formatting date:', error)
        return dateString
      }
    },
    isValidDate(dateString) {
      if (!dateString) return false
      try {
        const date = new Date(dateString)
        // Check for zero time (Go's zero time is 0001-01-01)
        if (date.getFullYear() <= 1) return false
        return !isNaN(date.getTime())
      } catch (error) {
        return false
      }
    },
    hasActiveModules(row) {
      return (
        (row.system_log && row.system_log.enabled) ||
        (row.resource_monitor && row.resource_monitor.enabled) ||
        (row.file_log && row.file_log.enabled)
      )
    },
    async loadForwarders() {
      this.loading = true
      try {
        const params = {
          page: this.currentPage,
          limit: this.currentPageSize,
          sort: this.currentSort.by,
          order: this.currentSort.order === 'asc' ? 'asc' : 'desc'
        }

        if (this.currentSearch) {
          params.filter = this.currentSearch
        }

        if (this.filters.statuses && this.filters.statuses.length > 0) {
          params.statuses = this.filters.statuses.join(',')
        }

        if (this.filters.modules && this.filters.modules.length > 0) {
          params.modules = this.filters.modules.join(',')
        }

        const response = await this.$http.get('/api/v1/forwarders', { params })

        if (response && response.data && response.data.data) {
          const result = response.data.data
          this.forwarders = Array.isArray(result.items) ? result.items : []
          this.totalItems = typeof result.total === 'number' ? result.total : 0
        } else {
          this.forwarders = []
          this.totalItems = 0
        }
      } catch (error) {
        console.error('Error loading forwarders:', error)
        this.$store.dispatch('NOTIFY', {
          type: 'error',
          text: this.$t('forwarders.loadError') || 'Failed to load forwarders'
        })
      } finally {
        this.loading = false
      }
    },
    handleSearch(searchQuery) {
      this.currentSearch = searchQuery
      this.currentPage = 1
      this.loadForwarders()
    },
    handleSort(sortConfig) {
      this.currentSort = {
        by: sortConfig.by || 'last_update',
        order: sortConfig.order || 'desc'
      }
      this.currentPage = 1
      this.loadForwarders()
    },
    handlePageChange(page) {
      this.currentPage = page
      this.loadForwarders()
    },
    handlePageSizeChange(pageSize) {
      this.currentPageSize = pageSize
      this.currentPage = 1
      this.loadForwarders()
    },
    handleEdit(item) {
      this.$emit('edit', item)
    },
    async handleToggleStatus(item) {
      const action = item.enabled ? this.$t('deactivate') : this.$t('activate')
      if (!window.confirm(this.$t('rus'))) return

      try {
        const payload = { ...item, enabled: !item.enabled }
        const response = await this.$http.patch(`/api/v1/forwarders/${item.id}`, payload)
        
        if (response && response.data) {
          this.$store.dispatch('NOTIFY', {
            type: 'success',
            text: response.data.message || `${action} thành công`
          })
          await this.loadForwarders()
        }
      } catch (error) {
        console.error('Error toggling forwarder status:', error)
        this.$store.dispatch('NOTIFY', {
          type: 'error',
          text: error.response?.data?.message || `${action} thất bại`
        })
      }
    },
    async handleDelete(item) {
      if (!window.confirm(this.$t('rus'))) return

      try {
        const response = await this.$http.delete(`/api/v1/forwarders/${item.id}`)
        
        if (response && response.data) {
          this.$store.dispatch('NOTIFY', {
            type: 'success',
            text: response.data.message || this.$t('deleteSuccess')
          })
          await this.loadForwarders()
        }
      } catch (error) {
        console.error('Error deleting forwarder:', error)
        this.$store.dispatch('NOTIFY', {
          type: 'error',
          text: error.response?.data?.message || this.$t('deleteError')
        })
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.forwarder-list-container {
  width: 100%;
}

.tags {
  display: flex;
  flex-wrap: wrap;
  gap: 0.25rem;

  .tag {
    margin: 0;
  }
}
</style>
